import type { Card } from '@ccc/shared-types';

/**
 * The v1.0 card catalog.
 *
 * Wording of `description` matches the player-facing text from the game brief
 * exactly — do not paraphrase. Edits land via the /admin card editor.
 *
 * Status: 'live' cards are draftable. 'coming_soon' shows up on the card
 * browser but isn't drawn into offers.
 */
export const CARD_CATALOG: Card[] = [
  // ============ PAWN ============
  {
    id: 'pawn-relentless-march',
    name: 'Pawn: Relentless March',
    category: 'pawn',
    shortTitle: 'Relentless March',
    description:
      'Your pawns may advance 1 or 2 squares at any time. Their very first move may be 3 squares, but if a pawn moves 3 squares, it cannot move on the following turn.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.pawn', 'starts.pawn'],
    effectSpecId: 'pawn-relentless-march',
  },
  {
    id: 'pawn-rewind-and-advance',
    name: 'Pawn: Rewind and Advance',
    category: 'pawn',
    shortTitle: 'Rewind and Advance',
    description:
      'Pawns can move backward indefinitely. When moving forward again, a pawn can retrace any backtracked path it has already traveled, plus one additional un-traversed square forward.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.pawn'],
    effectSpecId: 'pawn-rewind-and-advance',
  },
  {
    id: 'pawn-frontal-assault',
    name: 'Pawn: Frontal Assault',
    category: 'pawn',
    shortTitle: 'Frontal Assault',
    description:
      'Pawns can only capture straight ahead. They can promote one rank early, but not to a queen; unpromoted pawns can still reach the back rank to promote to a queen.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.pawn', 'capture.pawn', 'promotion'],
    effectSpecId: 'pawn-frontal-assault',
  },
  {
    id: 'pawn-omnipresent-passant',
    name: 'Pawn: Omnipresent Passant',
    category: 'pawn',
    shortTitle: 'Omnipresent Passant',
    description:
      "Pawns can execute an en passant capture anywhere on the board. Pawns can promote after en passant. A pawn promoting via en passant can only become a bishop or a knight. Normal back-rank promotion rules still apply.",
    flavor: 'No square is safe from the wind.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['capture.pawn', 'promotion'],
    effectSpecId: 'pawn-omnipresent-passant',
  },
  {
    id: 'pawn-vanguard-promotion',
    name: 'Pawn: Vanguard Promotion',
    category: 'pawn',
    shortTitle: 'Vanguard Promotion',
    description:
      'Pawns can promote early once they cross onto the enemy side of the board, but they can only promote to a knight. Additionally, you can have a maximum of 3 friendly knights on the board at any time.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['promotion'],
    effectSpecId: 'pawn-vanguard-promotion',
  },
  {
    id: 'pawn-ascendant-royalty',
    name: 'Pawn: Ascendant Royalty',
    category: 'pawn',
    shortTitle: 'Ascendant Royalty',
    description:
      'You do not lose the game when your king is captured; instead, you lose when your queen is captured. Pawns can promote to a king. If a queen has no allied king on the board, her movement is restricted to a maximum of 2 squares.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['loss-condition', 'promotion', 'movement.queen'],
    effectSpecId: 'pawn-ascendant-royalty',
  },
  {
    id: 'pawn-mini-rooks',
    name: 'Pawn: Mini-Rooks',
    category: 'pawn',
    shortTitle: 'Mini-Rooks',
    description:
      'All of your pawns become mini-rooks. Mini-rooks can only move 1 square horizontally or vertically, cannot castle, and will promote exclusively into regular rooks.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.pawn', 'starts.pawn', 'promotion'],
    effectSpecId: 'pawn-mini-rooks',
  },

  // ============ KNIGHT ============
  {
    id: 'knight-double-trot',
    name: 'Knight: Double Trot',
    category: 'knight',
    shortTitle: 'Double Trot',
    description:
      'Knights can move twice per turn, but the second move cannot result in a capture.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.knight', 'turn.bonus'],
    effectSpecId: 'knight-double-trot',
  },
  {
    id: 'knight-chill-knight',
    name: 'Knight: Chill Knight',
    category: 'knight',
    shortTitle: 'Chill Knight',
    description:
      'Knights gain the ability to move exactly 2 squares in any orthogonal direction and they can jump over intervening pieces. (basically they can move on all the straight version of the knight\'s path)',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.knight'],
    effectSpecId: 'knight-chill-knight',
  },
  {
    id: 'knight-tactical-swap',
    name: 'Knight: Tactical Swap',
    category: 'knight',
    shortTitle: 'Tactical Swap',
    description:
      'As a move, a knight can swap positions with any of your other pieces on the board.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.knight', 'special'],
    effectSpecId: 'knight-tactical-swap',
  },
  {
    id: 'knight-shackling-anchor',
    name: 'Knight: Shackling Anchor',
    category: 'knight',
    shortTitle: 'Shackling Anchor',
    description:
      'Knights lose the ability to capture. Instead, they anchor enemy pieces; any enemy piece sitting on a square that your knight can legally attack is frozen and cannot move.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['capture.knight', 'movement.opponent'],
    effectSpecId: 'knight-shackling-anchor',
  },

  // ============ BISHOP ============
  {
    id: 'bishop-vaulting-bishop',
    name: 'Bishop: Vaulting Bishop',
    category: 'bishop',
    shortTitle: 'Vaulting Bishop',
    description: 'Bishops can jump over exactly one piece per diagonal move.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.bishop'],
    effectSpecId: 'bishop-vaulting-bishop',
  },
  {
    id: 'bishop-cardinal-nudge',
    name: 'Bishop: Cardinal Nudge',
    category: 'bishop',
    shortTitle: 'Cardinal Nudge',
    description: 'Bishops can move exactly one square in any cardinal (orthogonal) direction.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.bishop'],
    effectSpecId: 'bishop-cardinal-nudge',
  },

  // ============ ROOK ============
  {
    id: 'rook-vaulting-rook',
    name: 'Rook: Vaulting Rook',
    category: 'rook',
    shortTitle: 'Vaulting Rook',
    description: 'Rooks can jump over exactly one piece per straight move.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.rook'],
    effectSpecId: 'rook-vaulting-rook',
  },
  {
    id: 'rook-literal-fortress',
    name: 'Rook: Literal Fortress',
    category: 'rook',
    shortTitle: 'Literal Fortress',
    description:
      "Both of your rooks can move once without exhausting your turn. However, the rooks can't capture at any given time, and the rooks cannot be captured at any given time by any piece except the enemy king.",
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['capture.rook', 'turn.bonus'],
    effectSpecId: 'rook-literal-fortress',
  },
  {
    id: 'rook-rook-zone',
    name: 'Rook: Rook Zone',
    category: 'rook',
    shortTitle: 'Rook Zone',
    description:
      "Any allied piece standing on the same file or rank as one of your rooks can use that rook's path of movement as its own move.",
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.all'],
    effectSpecId: 'rook-rook-zone',
  },

  // ============ QUEEN ============
  {
    id: 'queen-royal-cavalier',
    name: 'Queen: Royal Cavalier',
    category: 'queen',
    shortTitle: 'Royal Cavalier',
    description:
      'The queen can move like a knight, but her standard linear movement is restricted to a maximum of 6 squares.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.queen'],
    effectSpecId: 'queen-royal-cavalier',
  },
  {
    id: 'queen-short-range-burst',
    name: 'Queen: Short-Range Burst',
    category: 'queen',
    shortTitle: 'Short-Range Burst',
    description:
      'The queen can move and capture anywhere within a 2-square radius, and she can jump over pieces to do so.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.queen'],
    effectSpecId: 'queen-short-range-burst',
  },
  {
    id: 'queen-the-matriarchy',
    name: 'Queen: The Matriarchy',
    category: 'queen',
    shortTitle: 'The Matriarchy',
    description:
      "You have 2 queens, with 1 additional queen in the place of the king's side bishop. However, the king must be at the side of at least 1 queen at any time. If there's no queen at the king range, the king cannot move.",
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['starts.bishop', 'movement.king'],
    effectSpecId: 'queen-the-matriarchy',
  },

  // ============ KING ============
  {
    id: 'king-telekinetic-strike',
    name: 'King: Telekinetic Strike',
    category: 'king',
    shortTitle: 'Telekinetic Strike',
    description:
      'The king can capture an adjacent orthogonal piece without physically moving, remaining safely on his original square.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['capture.king', 'special'],
    effectSpecId: 'king-telekinetic-strike',
  },
  {
    id: 'king-phalanx-castle',
    name: 'King: Phalanx Castle',
    category: 'king',
    shortTitle: 'Phalanx Castle',
    description:
      'After castling, the king and rook are permanently tied together. The rook can move a maximum of 2 squares straight. You may choose to move either the king or the rook, but the unit cannot move if either piece bumps into a friendly obstacle. Both pieces retain the ability to capture.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.king', 'movement.rook', 'special'],
    effectSpecId: 'king-phalanx-castle',
  },

  // ============ GENERAL ============
  {
    id: 'general-momentum-check',
    name: 'Momentum Check',
    category: 'general',
    shortTitle: 'Momentum Check',
    description:
      'Whenever one of your pieces delivers a check, you may immediately move a second piece, though this bonus move cannot result in a capture.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['turn.bonus'],
    effectSpecId: 'general-momentum-check',
  },
  {
    id: 'general-vaulting-army',
    name: 'Vaulting Army',
    category: 'general',
    shortTitle: 'Vaulting Army',
    description:
      'All of your pieces can jump over a single piece, but they cannot capture on a turn where they choose to jump.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.all'],
    effectSpecId: 'general-vaulting-army',
  },
  {
    id: 'general-retaliatory-strike',
    name: 'Retaliatory Strike',
    category: 'general',
    shortTitle: 'Retaliatory Strike',
    description:
      'When one of your pieces is captured, you may immediately make a bonus move with any other piece using its standard movement rules, but this move cannot result in a capture.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['turn.bonus'],
    effectSpecId: 'general-retaliatory-strike',
  },
  {
    id: 'general-knighted-retinue',
    name: 'Knighted Retinue',
    category: 'general',
    shortTitle: 'Knighted Retinue',
    description:
      'All of your promoted pawns retain the ability to move like a knight in addition to the standard moves of their newly promoted piece.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['movement.all', 'promotion'],
    effectSpecId: 'general-knighted-retinue',
  },
  {
    id: 'general-stored-tempo',
    name: 'Stored Tempo',
    category: 'general',
    shortTitle: 'Stored Tempo',
    description:
      'You may choose to pass your turn to store 0.5 tempo. You can spend 1 full tempo to make an extra move immediately after your normal turn, however you cannot move the same piece twice in a row.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['turn.bonus'],
    effectSpecId: 'general-stored-tempo',
  },
  {
    id: 'general-flexible-deployment',
    name: 'Flexible Deployment',
    category: 'general',
    shortTitle: 'Flexible Deployment',
    description:
      'When promoting a pawn, you can place the newly promoted piece on any empty square within your back two ranks instead of the promotion square. Additionally, a pawn on its 7th rank (2nd rank for Black) can move one square forward to capture a defender and promote immediately.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['promotion'],
    effectSpecId: 'general-flexible-deployment',
  },
  {
    id: 'general-royal-escort',
    name: 'Royal Escort',
    category: 'general',
    shortTitle: 'Royal Escort',
    description:
      'Whenever your king makes a move, you may also move any one of your other currently threatened pieces by exactly one square.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['turn.bonus', 'movement.king'],
    effectSpecId: 'general-royal-escort',
  },

  {
    id: 'general-castle-master',
    name: 'Castle Master',
    category: 'general',
    shortTitle: 'Castle Master',
    description:
      'You can castle multiple times per game. Your castling rights never expire. You can castle with any of your pieces. You can castle vertically. You can castle at any time, anywhere on the board as long as the path between them is clear.',
    rulesetVersion: 1,
    status: 'live',
    conflictTags: ['special'],
    effectSpecId: 'general-castle-master',
  },
];

/** Lookup by id. */
export function getCard(id: string): Card | undefined {
  return CARD_CATALOG.find((c) => c.id === id);
}

/** All cards eligible to be drafted. */
export function liveCards(): Card[] {
  return CARD_CATALOG.filter((c) => c.status === 'live');
}

/** Group cards by category for the deck browser UI. */
export function cardsByCategory(): Record<string, Card[]> {
  const out: Record<string, Card[]> = {};
  for (const c of CARD_CATALOG) {
    if (!out[c.category]) out[c.category] = [];
    out[c.category]!.push(c);
  }
  return out;
}
